/**
 * Уровень ведения для разделов, которые ведутся по хозяйству
 * (расценки работ, аутсорс, готовая продукция).
 *
 * История: до 24.08 — все хозяйства пилюлями в строку (на проде их
 * шестьдесят); потом — штатная шторка с деревом «кластер → хозяйство»
 * (AK-745). С AK-866 В-3 хозяйство на экранах не выбирают вовсе — оно одно,
 * в переключателе вверху левого меню (AK-897). Поэтому здесь уже НЕ выбор
 * хозяйства, а выбор УРОВНЯ из двух:
 *
 * 1. **«Шаблон компании»** — не область видимости, а отдельное место: строки
 *    бюджета без `farm_id`, из которых копируют в хозяйства. Подменять их
 *    «всей компанией» нельзя — шаблон это заготовка, а не место правды.
 * 2. **Хозяйство из переключателя** — его собственные цифры. Сменить
 *    хозяйство — в переключателе; в переключателе компания или кластер —
 *    кнопка хозяйства недоступна и объясняет, где выбрать.
 *
 * `POST /scope/set` отсюда не зовётся: уровень — фильтр этого экрана.
 */
import { LayoutTemplate, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PICK_FARM_TEXT } from '@/components/scope/FarmScopeNotice';
import { fmtScopeArea, scopeArableHa, type ScopeInfo } from '@/components/scope/ScopeTree';
import { SECTION_TITLES, type BudgetSection } from '@/lib/planning/budgetCatalog';

/** Уровень ведения раздела: шаблон компании или хозяйство из переключателя. */
export type BudgetLevel = 'template' | 'farm';

export interface FarmScopeBarProps {
  /** Хозяйство из переключателя; `null` — в переключателе компания/кластер. */
  farm: ScopeInfo | null;
  value: BudgetLevel;
  onChange: (level: BudgetLevel) => void;
  section: BudgetSection;
  loading?: boolean;
}

export function FarmScopeBar({ farm, value, onChange, section, loading }: FarmScopeBarProps) {
  const isTemplate = value === 'template' || !farm;
  // Мера подписи — ПАШНЯ, как в переключателе и в дереве (AK-752).
  const area = fmtScopeArea(farm ? scopeArableHa(farm) : null);

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted p-3">
      <span className="text-sm text-muted-foreground">Ведём для:</span>

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Уровень ведения">
        <Button
          type="button"
          size="sm"
          variant={isTemplate ? 'default' : 'outline'}
          aria-pressed={isTemplate}
          onClick={() => onChange('template')}
        >
          <LayoutTemplate className="h-4 w-4 shrink-0" />
          Шаблон компании
        </Button>
        <Button
          type="button"
          size="sm"
          variant={!isTemplate ? 'default' : 'outline'}
          aria-pressed={!isTemplate}
          disabled={!farm || loading}
          onClick={() => onChange('farm')}
        >
          <MapPin className="h-4 w-4 shrink-0" />
          <span className="max-w-60 truncate">{farm?.name ?? 'Хозяйство'}</span>
          {area && <span className="text-xs opacity-80">{area}</span>}
        </Button>
      </div>

      <span className="text-sm text-muted-foreground">
        {!farm
          ? `${PICK_FARM_TEXT}, чтобы вести его свои цифры.`
          : isTemplate
            ? 'Шаблон — заготовка, из которой копируют в хозяйства. Цифры хозяйств он не меняет.'
            : 'Свои цифры хозяйства.'}
      </span>

      <span className="ml-auto text-xs text-muted-foreground">
        {SECTION_TITLES[section]} — у каждого хозяйства свои цифры
      </span>
    </div>
  );
}
